import React from 'react';
import styles from './settings.module.scss';
import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Button from '@smart-tv/components/button/button';
import { DialogItem } from '@smart-tv/components/dialog';
import { HEADER_SETTINGS } from '@smart-tv/components/header/header-helpers';
import Typography from '@smart-tv/components/typography';
import testIds from '@smart-tv/e2e/shared/variables';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import { tabs } from './settings';
import SettingsLogoutDialog from './settings-logout-dialog';
import Npvr from '@smart-tv/components/npvr';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import NotificationDot from '@smart-tv/components/notification-dot';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';
import { NpvrTab } from '@smart-tv/components/npvr/npvr-helpers';
import { useLocation, useNavigate } from 'react-router-dom';

export const SETTINGS_CHANNELLIST_TAB_FOCUS_KEY = 'SettingsChannelListTabFocusKey';
export const SETTINGS_NPVR_TAB_FOCUS_KEY = 'SettingsNpvrTabFocusKey';

interface Props {
  handleEnterPress: (tab: number | undefined) => void;
  selectedTab?: number;
}

export const SettingsMenu: React.FC<Props> = (props: Props) => {
  const { handleEnterPress, selectedTab } = props;
  const location = useLocation();
  const initialNpvrTab = location.state?.openNpvrTab as NpvrTab | undefined;
  const [logoutDialog, setLogoutDialog] = React.useState(false);
  const [npvrDialog, setNpvrDialog] = React.useState(!!initialNpvrTab);
  const [npvrInitialTab, setNpvrInitialTab] = React.useState<NpvrTab | undefined>(initialNpvrTab);
  const { handleOpen } = useFavoritesEdit();
  const navigate = useNavigate();
  const { refresh: refreshNpvrNotifications, hasAnyNotification: hasNpvrNotification } =
    useNpvrNotifications();

  const { ref, focusKey } = useFocusable();

  React.useEffect(() => {
    refreshNpvrNotifications();
  }, [refreshNpvrNotifications]);

  React.useEffect(() => {
    const openNpvrTab: NpvrTab | undefined = location.state && location.state.openNpvrTab;
    if (!openNpvrTab) return;

    setFocus(SETTINGS_NPVR_TAB_FOCUS_KEY);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const handleKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      if (isBackKey(event) && !logoutDialog && !npvrDialog) {
        setFocus(HEADER_SETTINGS);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },
    [logoutDialog, npvrDialog]
  );

  const handleFavoritesPressed = React.useCallback(() => {
    handleEnterPress(undefined);
    handleOpen();
  }, [handleEnterPress, handleOpen]);

  const handleNpvrPressed = React.useCallback(() => {
    handleEnterPress(undefined);
    setNpvrInitialTab(undefined);
    setNpvrDialog(true);
  }, [handleEnterPress]);

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyPress(event));

  return (
    <React.Fragment>
      <FocusContext.Provider value={focusKey}>
        <div className={styles['tabs']} ref={ref}>
          <Typography
            className={styles['title']}
            variant="h2"
            data-testid={testIds.settings.header}
          >
            Indstillinger
          </Typography>
          <DialogItem
            selected={selectedTab === tabs.CHANNELLIST}
            onEnterPress={() => handleFavoritesPressed()}
            focusKey={SETTINGS_CHANNELLIST_TAB_FOCUS_KEY}
            itemProps={{ 'data-testid': testIds.settings.tabs.favoritesList }}
          >
            Kanallister
          </DialogItem>
          <DialogItem
            focusKey={SETTINGS_NPVR_TAB_FOCUS_KEY}
            onEnterPress={() => handleNpvrPressed()}
            itemProps={{ 'data-testid': testIds.settings.tabs.npvr }}
          >
            Optagelser
            {hasNpvrNotification && <NotificationDot />}
          </DialogItem>
          <DialogItem
            selected={selectedTab === tabs.TECHNICAL}
            onEnterPress={() => handleEnterPress(tabs.TECHNICAL)}
            itemProps={{ 'data-testid': testIds.settings.tabs.tech }}
          >
            Teknisk info
          </DialogItem>
          <DialogItem
            selected={selectedTab === tabs.OPEN_SOURCE}
            onEnterPress={() => handleEnterPress(tabs.OPEN_SOURCE)}
            itemProps={{ 'data-testid': testIds.settings.tabs.openSource }}
          >
            Open Source
          </DialogItem>
          <DialogItem
            selected={selectedTab === tabs.GDPR}
            onEnterPress={() => handleEnterPress(tabs.GDPR)}
            itemProps={{ 'data-testid': testIds.settings.tabs.gdpr }}
          >
            Persondatapolitik
          </DialogItem>
          <Button
            className={styles['logout']}
            onClick={() => setLogoutDialog(true)}
            data-testid={testIds.settings.logout}
          >
            Log ud
          </Button>
        </div>
      </FocusContext.Provider>
      <SettingsLogoutDialog open={logoutDialog} onClose={() => setLogoutDialog(false)} />
      <Npvr open={npvrDialog} initialTab={npvrInitialTab} onClose={() => setNpvrDialog(false)} />
    </React.Fragment>
  );
};
export default SettingsMenu;
